import React, { useState } from "react";
import {
  ArrowIcon,
  BookmarkIconWrapper,
  CallToActionContainer,
  CompareIconWrapper,
  Container,
  ContentTitle,
  ContentWrapper,
  DetailContainer,
  DetailsSection,
  Header,
  SubTitle,
  Title,
  PartnerLocationIcon,
  TitleContainer,
} from "./HospitalListCard.styled";
import { ReactComponent as Arrow } from "../../Assets/Icons/Arrow.svg";
import { ReactComponent as Bookmark } from "../../Assets/Icons/Bookmark.svg";
import { ReactComponent as Hospital } from "../../Assets/Icons/Hospital.svg";
import { ReactComponent as Beds } from "../../Assets/Icons/Beds.svg";
import { ReactComponent as Badge } from "../../Assets/Icons/Badge.svg";
import { ReactComponent as Currency } from "../../Assets/Icons/Currency.svg";
import { ReactComponent as Discharges } from "../../Assets/Icons/Discharges.svg";
import { ReactComponent as Ratings } from "../../Assets/Icons/Ratings.svg";
import { ReactComponent as SliderIconGray } from "../../Assets/Icons/SliderIconGray.svg";
import { ReactComponent as SelectedCompareHospital } from "../../Assets/Icons/SelectedCompareHospital.svg";
import { ReactComponent as PartnerLocation } from "../../Assets/Icons/PartnerLocation.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useAddBookmark, useRemoveBookmark } from "../../hooks/useBookmarks";
import {
  addHospitalToCompare,
  removeHospitalFromCompare,
} from "../../Redux/compare/compare.actions";
import { Loader } from "../../App";
import {
  formatNumber,
  toTitleCase,
  viewSizeCalculator,
} from "../../utils";
import Info from "../Info";
import { useSnack } from "../SnackBar";

const HospitalListCard = ({
  data,
  bookmarkedItemsSet,
  sortingLabel,
  toggleState,
  }) => {
  const {
    hosp10_name: hospitalName = "Unknown Hospital",
    city: location = "Unknown Location",
    health_system_name: healthSystemName = "N/A",
    calc_stats_adj_pt_discharge: patientDischarges = 0,
    calc_medicaid_gross_revenue: grossRevenue = 0,
    net_patient_revenue: netPatientRevenue = "0",
    prov_control_type: ownership = 0,
    calc_stats_beds_total: bedCount = 0,
    provider_number: providerNumber = 0,
    state: state = "n/a",
    accreditation_count: accreditationCount = 0,
    score: score = 0,
    ranking: ranking = 0,
  } = data || {};
  const formattedNetRevenue = netPatientRevenue
    ? netPatientRevenue.toString().replace(/\$|,/g, "").split(".")[0]
    : "0";

  const dispatch = useDispatch();
  const addBookmark = useAddBookmark();
  const removeBookmark = useRemoveBookmark();
  const navigate = useNavigate();
  const snack = useSnack();
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const { bookmarkList } = useSelector((state) => state.bookmarks);
  const { compareHospitals } = useSelector((state) => state.compare);
  const bookmarksNamesList =
    bookmarkList?.map((el) => el.name?.toLowerCase()) || [];

  const isBookmarked = bookmarksNamesList.includes(hospitalName?.toLowerCase());

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setCollapsed(!collapsed);
  };

  const navigateToFactSheet = (e) => {
    e.stopPropagation();
    if (providerNumber) navigate(`/hospitals/${providerNumber}`);
  };

  const addToBookmark = async () => {
    setLoading(true);
    const res = await addBookmark(providerNumber);
    
    if (res.success) {
      snack("Bookmark added successfully");
    } else {
        snack(
          `Failed to Add bookmark: ${res.message}`,
          "#FFC640",
          "#333333"
        );
    }

    setLoading(false);
  };

  const removeFromBookmark = async () => {
    setLoading(true);
    const res = await removeBookmark(providerNumber);
    
    if (res.success) {
      snack("Bookmark removed successfully");
    } else {
      snack(
        `Failed to remove: ${res.message}`,
        "#FFC640",
        "#333333"
      );
    }
    setLoading(false);
  };

  const handleBookmarkToggle = () => {
    isBookmarked ? removeFromBookmark() : addToBookmark();
  };

  const isAddedToCompare = (id) => {
    return compareHospitals.some((el) => el.provider_number === id);
  };

  const addToCompare = (data) => {
    if (compareHospitals.length >= 20) {
        snack(
          "Max Selection reached! Up to 20 Hospitals can be compared",
          "#FFC640",
          "#333333"
        );
        return;
      }
    const payload = {
      value: data["hosp10_name"],
      label: toTitleCase(data["hosp10_name"]),
      location:
        data.city && data.state
          ? `${data.city
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())}, ${data.state}`
          : "N/A",
      provider_number: data?.provider_number,
    };
    const updatedCompareCount = compareHospitals.length + 1;

    dispatch(addHospitalToCompare(payload));

      snack(
        `View all (${updatedCompareCount}) Hospital added in Compare screen!`,
        "#1F1F1F",
        "#FFFFFF",
        "View",
        () => {
          if (updatedCompareCount < 2) {
            snack(
              "Select at least two hospitals to compare!",
              "#FFC640",
              "#333333"
            );
          } else {
            toggleState("isTableVisible");
          }
        }
      );
  };

  const removeFromCompare = (id) => {
    dispatch(removeHospitalFromCompare(id));
    snack("Hospital removed from Compare screen!");
  };

  const mappingSortingLabels = {
    "Inpatient Discharges": `${sortingLabel}: ${Math.floor(
      patientDischarges,
    ).toLocaleString()}`,
    "Gross Revenue": `${sortingLabel}: ${grossRevenue ? formatNumber(`${grossRevenue}`) : formatNumber(formattedNetRevenue)}`,
    "CMS Rating (>4)": "CMS Rating (>4)",
    "No. of Beds": `${bedCount} Beds`,
    Accreditations: "Accreditations",
    "Ownership Type (For Profit)": ownership,
    "Net Patient Revenue": `${sortingLabel}: ${netPatientRevenue ? formatNumber(`${netPatientRevenue}`) : formatNumber(netPatientRevenue)}`,
    "Score": `${sortingLabel}: ${score}`,
    "Ranking": `${sortingLabel}: ${ranking}`,
  };

  return (
    <Container collapsed={collapsed}>
      <Header>
        <TitleContainer
          onClick={collapsed ? toggleCollapse : navigateToFactSheet}
        >
          <ArrowIcon collapsed={collapsed} onClick={toggleCollapse}>
            <Arrow />
          </ArrowIcon>
         <div>
            <Title showHover={!collapsed} collapsed={collapsed}>
              {Number(data?.esa) === 1 && (
                <PartnerLocationIcon>
                  <PartnerLocation />
                </PartnerLocationIcon>
              )}

              <span>{hospitalName?.toLowerCase()}</span>
            </Title>
            <SubTitle
              onClick={collapsed ? toggleCollapse : navigateToFactSheet}
            >
              {mappingSortingLabels[sortingLabel]
              ? `${toTitleCase(location)}, ${state} |`
              : `${toTitleCase(location)}, ${state}`}
              {/* Commented becuase this is part of Phase 2 */}
              {/* {mappingSortingLabels[sortingLabel]
                ? `${healthSystemName || "N/A"} | ${toTitleCase(location)}, ${state} |`
                : `${healthSystemName || "N/A"} | ${toTitleCase(location)}, ${state}`} */}
              <span> {mappingSortingLabels[sortingLabel]}</span>
          </SubTitle>
          </div>
        </TitleContainer>

        <CallToActionContainer
          isBookmarked={isBookmarked}
          isCompared={isAddedToCompare(data?.provider_number)}
        >
          <Info
            showArrow={true}
            config={{
              tooltipText: isAddedToCompare(data?.provider_number)
                ? "Remove from Compare"
                : "Add to Compare",
              position: "top",
              offsetY: 10,
            }}
          >
            <CompareIconWrapper
              isCompared={isAddedToCompare(data?.provider_number)}
              collapsed={collapsed}
            >
              {!isAddedToCompare(data?.provider_number) && (
                <SliderIconGray onClick={() => addToCompare(data)} />
              )}

              {isAddedToCompare(data?.provider_number) && (
                <SelectedCompareHospital
                  onClick={() => removeFromCompare(data?.provider_number)}
                />
              )}
            </CompareIconWrapper>
          </Info>

          <BookmarkIconWrapper
            isBookmarked={isBookmarked}
            collapsed={collapsed}
            loading={loading}
          >
            {loading ? (
              <Loader 
                height={viewSizeCalculator(1.46)}
                width={viewSizeCalculator(1.46)}
              />
            ) : (
              <Info
                showArrow={false}
                config={{
                  tooltipText: "Bookmark",
                  position: "top",
                  offsetY: 10,
                }}
                position="start"
              >
                <Bookmark
                  onClick={handleBookmarkToggle}
                  stroke={isBookmarked ? "none" : "#B0B0B0"}
                  style={{
                    fill: isBookmarked ? "#F6B51C" : "none",
                    color: isBookmarked ? "#F6B51C" : "#333333",
                    marginTop: viewSizeCalculator(-0.43),
                  }}
                />
              </Info>
            )}
          </BookmarkIconWrapper>
        </CallToActionContainer>
      </Header>

      <DetailsSection onClick={navigateToFactSheet} collapsed={collapsed}>
        <DetailContainer>
          <ContentWrapper>
            <Info
              showArrow={false}
              config={{
                tooltipText: "Ownership Type",
                position: "top",
              }}
            >
              <Hospital />
            </Info>
            <ContentTitle>{ownership || "N/A"}</ContentTitle>
          </ContentWrapper>
          <ContentWrapper>
            <Info
              showArrow={false}
              config={{
                tooltipText: "No. of Beds",
                position: "top",
              }}
            >
              <Beds />
            </Info>
            <ContentTitle>{bedCount || 0} Beds</ContentTitle>
          </ContentWrapper>
          {/* <ContentWrapper>
            <Discharges />
            <ContentTitle>
              {Math.floor(patientDischarges).toLocaleString()} Discharges
            </ContentTitle>
          </ContentWrapper> */}
        </DetailContainer>
        <DetailContainer>
          <ContentWrapper>
            <Info
              showArrow={false}
              config={{
                tooltipText: "Accreditations",
                position: "top",
              }}
            >
              <Badge />
            </Info>
            <ContentTitle>{accreditationCount} Accreditations</ContentTitle>
          </ContentWrapper>
          <ContentWrapper>
            <Info
              showArrow={false}
              config={{
                tooltipText: `${netPatientRevenue ? "Net Patient Revenue" : "Gross Revenue"}`,
                position: "top",
              }}
            >
              <Currency />
            </Info>
            <ContentTitle>
              {grossRevenue
                ? formatNumber(`${grossRevenue}`)
                : formatNumber(formattedNetRevenue)}{" "}
            </ContentTitle>
          </ContentWrapper>
          {/* <ContentWrapper>
            <Ratings />
            <ContentTitle>CMS - 5</ContentTitle>
          </ContentWrapper> */}
        </DetailContainer>
      </DetailsSection>
    </Container>
  );
};

export default HospitalListCard;
